/**
 * AMP Statement Extractor - PDF Extraction Module (Browser/pdfjs-dist)
 *
 * Parses an AMP statement PDF into one or more AccountStatement objects.
 * A single PDF can hold several accounts (e.g. 186035 / 186035A) and each
 * account can contain three sections, in order:
 *   - TRADES CONFIRMATIONS  (individual fills + per-block fee breakdown)
 *   - PURCHASE & SALE        (matched round-turns + per-block realized P&L)
 *   - JOURNAL ENTRIES        (payments / inter-account transfers)
 *   - Account Summary        (authoritative balances, P&L, commission)
 *
 * Everything is read from word x/y positions; no OCR. The section is tracked
 * as a state so BUY/SELL column positions and row shapes are interpreted
 * correctly (the two sections use different columns), and expiration is read
 * per block (each block's TOTAL line carries its own EX- date).
 */

import * as pdfjsLib from 'pdfjs-dist';
import type { TextItem, TextMarkedContent } from 'pdfjs-dist/types/src/display/api';
import type {
  Trade, ExtractionResult, PdfWord, PdfLine, ExtractorConfig,
  AccountStatement, ConfirmationBlock, PsBlock,
  AccountSummary, FeeBreakdown, FeeLine,
} from './types';
import { parseContractInfo } from './symbol-resolver';
import { computeEconomics, aggregateFees, reconcile, computeFifoPositions } from './derive';

const PDFJS_VERSION = '4.10.38';
pdfjsLib.GlobalWorkerOptions.workerSrc =
  `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.mjs`;

// Column x-positions (empirically derived). Confirmations and P&S differ.
export const DEFAULT_CONFIG: ExtractorConfig = {
  confBuyXMin: 158, confBuyXMax: 192,
  confSellXMin: 198, confSellXMax: 232,
  psBuyXMin: 158, psBuyXMax: 188,
  psSellXMin: 198, psSellXMax: 220,
};

const DATE_RE = /^\d{2}-[A-Z]{3}-\d{2}$/;
const ACCOUNT_RE = /^\d{4,8}[A-Z]?$/;
const MARKETS = ['CME', 'COMEX', 'NYMEX', 'CBOT'];

type Section = 'NONE' | 'CONFIRMATION' | 'PS' | 'JOURNAL' | 'SUMMARY';

export async function extractFromFile(
  file: File,
  config: Partial<ExtractorConfig> = {}
): Promise<ExtractionResult> {
  const arrayBuffer = await file.arrayBuffer();
  return extractWithPdfjs(new Uint8Array(arrayBuffer), config);
}

export async function extractWithPdfjs(
  pdfBuffer: Uint8Array,
  config: Partial<ExtractorConfig> = {}
): Promise<ExtractionResult> {
  const cfg = { ...DEFAULT_CONFIG, ...config };

  try {
    const loadingTask = pdfjsLib.getDocument({
      data: pdfBuffer,
      useSystemFonts: true,
      isEvalSupported: false,
      disableFontFace: true,
    });
    const pdfDoc = await loadingTask.promise;

    // Collect lines for every page, plus the account number per page.
    // Only the account number is read from the header; client name/address (PII)
    // are deliberately never parsed.
    const allLines: PdfLine[] = [];
    const accountByPage = new Map<number, string>();
    let lastAccount = '';

    for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
      const page = await pdfDoc.getPage(pageNum);
      const textContent = await page.getTextContent();
      const viewport = page.getViewport({ scale: 1.0 });
      const words = extractWordsWithPositions(textContent.items, viewport.height, pageNum);

      const acct = detectAccountNumber(words) || lastAccount;
      if (acct) lastAccount = acct;
      accountByPage.set(pageNum, acct);

      for (const line of groupWordsIntoLines(words)) allLines.push(line);
    }

    const accounts = parseAccounts(allLines, accountByPage, cfg);

    if (accounts.length === 0) {
      return { success: false, accounts: [], error: 'No accounts/trades extracted', method: 'pdfjs' };
    }
    return { success: true, accounts, method: 'pdfjs' };
  } catch (error) {
    console.error('pdfjs extraction error:', error);
    return {
      success: false,
      accounts: [],
      error: error instanceof Error ? error.message : String(error),
      method: 'pdfjs',
    };
  }
}

/** Walk every line in document order, grouped by account, tracking the section. */
export function parseAccounts(
  lines: PdfLine[],
  accountByPage: Map<number, string>,
  cfg: ExtractorConfig
): AccountStatement[] {
  const byNumber = new Map<string, AccountStatement>();
  const order: string[] = [];

  let acctNum = '';
  let acct: AccountStatement | null = null;
  let section: Section = 'NONE';

  // Pending block state (per current account/section)
  let pendingConfFills: Trade[] = [];
  let lastConf: ConfirmationBlock | null = null;
  let pendingPsFills: Trade[] = [];
  let psDate = '';
  let confDate = '';

  const getAcct = (num: string): AccountStatement => {
    let a = byNumber.get(num);
    if (!a) {
      a = blankAccount(num);
      byNumber.set(num, a);
      order.push(num);
    }
    return a;
  };

  const closeConf = () => {
    // fills accumulated but no TOTAL seen (rare) -> drop into a loose block
    pendingConfFills = [];
  };
  const closePs = () => { pendingPsFills = []; };

  for (const line of lines) {
    const pageAcct = accountByPage.get(line.page) || acctNum;
    if (pageAcct && pageAcct !== acctNum) {
      // account boundary
      closeConf(); closePs(); lastConf = null;
      acctNum = pageAcct;
      acct = getAcct(acctNum);
      section = 'NONE';
    }
    if (!acct) { acct = getAcct(pageAcct || 'UNKNOWN'); acctNum = acct.accountNumber; }

    const words = [...line.words].sort((a, b) => a.x - b.x);
    if (words.length === 0) continue;
    const compact = words.map(w => w.text).join('').replace(/\s+/g, '').toUpperCase();
    const lineText = words.map(w => w.text).join(' ');

    // --- Section transitions ---
    const marker = detectMarker(compact);
    if (marker) {
      if (marker !== section) { closeConf(); closePs(); }
      section = marker;
      // capture period dates from banners ("FROM 01/01/26 TO 01/31/26" or DD-MON-YY)
      capturePeriod(acct, lineText);
      // P&S / confirmation day headers also carry the "AS OF <date>"
      const asOf = lineText.match(/AS OF\s+(\d{2}-[A-Z]{3}-\d{2})/i);
      if (asOf) { if (section === 'PS') psDate = asOf[1]; else if (section === 'CONFIRMATION') confDate = asOf[1]; }
      continue;
    }

    // Skip column header rows
    if (/^DATE\b/i.test(lineText) || compact.startsWith('DATEVALUE') || compact.startsWith('DATETRADE')) continue;

    if (section === 'CONFIRMATION') {
      handleConfirmationLine(words, lineText, compact, acct, cfg, {
        getDate: () => confDate,
        getPendingFills: () => pendingConfFills,
        setPendingFills: (f) => { pendingConfFills = f; },
        getLastConf: () => lastConf,
        setLastConf: (b) => { lastConf = b; },
        setDate: (d) => { confDate = d; },
      });
    } else if (section === 'PS') {
      handlePsLine(words, lineText, compact, acct, cfg, {
        getDate: () => psDate,
        getPendingFills: () => pendingPsFills,
        setPendingFills: (f) => { pendingPsFills = f; },
        setDate: (d) => { psDate = d; },
      });
    } else if (section === 'JOURNAL') {
      handleJournalLine(words, lineText, acct);
    } else if (section === 'SUMMARY') {
      handleSummaryLine(words, lineText, compact, acct);
    }
  }

  // Finalize derived data per account
  const result = order.map(num => byNumber.get(num)!);
  for (const a of result) {
    // Canonical executions: Confirmation fills have unique ids and cover every
    // execution. P&S re-lists the same ids split into matched lots, so use it
    // only when there is no Confirmations section (avoids duplicate exec ids).
    const confFills = a.confirmations.flatMap(b => b.fills);
    a.trades = confFills.length ? confFills : a.psBlocks.flatMap(b => b.fills);
    a.buyQty = a.confirmations.reduce((s, b) => s + b.buyQty, 0);
    a.sellQty = a.confirmations.reduce((s, b) => s + b.sellQty, 0);
    a.fees = aggregateFees(a.confirmations.map(b => b.fees));
    a.economics = computeEconomics(a.confirmations, a.psBlocks);
    // Attribute per-side commission to each execution (fee/contract * qty).
    const feeByContract = new Map(a.economics.map(e => [e.contractType, e.feePerContract]));
    for (const t of a.trades) {
      const f = feeByContract.get(t.Contract_Type);
      t.Commission = f != null ? r2(f * t.Quantity) : 0;
    }
    const fifo = computeFifoPositions(a.trades, a.economics);
    a.positions = fifo.closed;
    a.openPositions = fifo.open;
    const receipts = a.journal.filter(j => j.drcr === 'CR').reduce((s, j) => s + j.amount, 0);
    const payments = a.journal.filter(j => j.drcr === 'DR').reduce((s, j) => s + j.amount, 0);
    const signed = (j: typeof a.journal[number]) => (j.drcr === 'CR' ? j.amount : -j.amount);
    const catSum = (c: string) => a.journal.filter(j => j.category === c).reduce((s, j) => s + signed(j), 0);
    a.cashFlow = {
      receipts: r2(receipts), payments: r2(payments), net: r2(receipts - payments),
      deposits: r2(catSum('DEPOSIT')), withdrawals: r2(catSum('WITHDRAWAL')),
      transfersNet: r2(catSum('TRANSFER')), dataFees: r2(catSum('DATA_FEE')),
    };
    a.reconciliation = reconcile(a.psBlocks, a.fees, a.cashFlow, a.summary);
    a.reconciliation.flat = a.openPositions.length === 0;
    a.reconciliation.openTradeEquity = a.summary?.openTradeEquity ?? null;
  }
  return result;
}

function r2(v: number): number { return Math.round(v * 100) / 100; }

// ---------------------------------------------------------------------------
// Section handlers
// ---------------------------------------------------------------------------

interface ConfState {
  getDate(): string;
  getPendingFills(): Trade[];
  setPendingFills(f: Trade[]): void;
  getLastConf(): ConfirmationBlock | null;
  setLastConf(b: ConfirmationBlock | null): void;
  setDate(d: string): void;
}

function handleConfirmationLine(
  words: PdfWord[], lineText: string, _compact: string,
  acct: AccountStatement, cfg: ExtractorConfig, st: ConfState
): void {
  // Day header
  const asOf = lineText.match(/EXECUTED AS OF\s+(\d{2}-[A-Z]{3}-\d{2})/i);
  if (asOf) { st.setDate(asOf[1]); return; }

  // TOTAL row -> close the pending fill block into a ConfirmationBlock
  if (/^TOTAL\b/.test(lineText)) {
    const fills = st.getPendingFills();
    const exp = (lineText.match(/EX-\s*(\d{2}-[A-Z]{3}-\d{2})/) || [])[1] || '';
    const buyTot = findIntInRange(words, cfg.confBuyXMin, cfg.confBuyXMax);
    const sellTot = findIntInRange(words, cfg.confSellXMin, cfg.confSellXMax);
    const proto = fills[0];
    const block: ConfirmationBlock = {
      account: acct.accountNumber,
      date: proto?.Date || st.getDate(),
      contractType: proto?.Contract_Type || 'UNKNOWN',
      contractMonth: proto?.Contract_Month || '',
      expiration: exp,
      buyQty: buyTot ?? fills.filter(f => f.Direction === 'BUY').reduce((s, f) => s + f.Quantity, 0),
      sellQty: sellTot ?? fills.filter(f => f.Direction === 'SELL').reduce((s, f) => s + f.Quantity, 0),
      avgLong: null, avgShort: null,
      fees: emptyFees(),
      fills,
    };
    for (const f of fills) f.Expiration = exp;
    acct.confirmations.push(block);
    st.setLastConf(block);
    st.setPendingFills([]);
    return;
  }

  // AVERAGE LONG / AVERAGE SHORT
  if (/AVERAGE LONG/i.test(lineText)) {
    const v = lastNumber(words); const b = st.getLastConf();
    if (b && v !== null) b.avgLong = v;
    return;
  }
  if (/AVERAGE SHORT/i.test(lineText)) {
    const v = lastNumber(words); const b = st.getLastConf();
    if (b && v !== null) b.avgShort = v;
    return;
  }

  // Fee line: label at x~400-470, USD, amount, DR/CR
  const fee = parseFeeLine(words);
  if (fee) {
    const b = st.getLastConf();
    if (b) { b.fees.lines.push(fee); foldFee(b.fees, fee); }
    return;
  }

  // Trade fill row (date-led)
  if (DATE_RE.test(words[0].text)) {
    const trade = parseFill(words, acct.accountNumber, 'CONFIRMATION', cfg);
    if (trade) {
      const fills = st.getPendingFills();
      fills.push(trade);
      st.setPendingFills(fills);
    }
  }
}

interface PsState {
  getDate(): string;
  getPendingFills(): Trade[];
  setPendingFills(f: Trade[]): void;
  setDate(d: string): void;
}

function handlePsLine(
  words: PdfWord[], lineText: string, _compact: string,
  acct: AccountStatement, cfg: ExtractorConfig, st: PsState
): void {
  const asOf = lineText.match(/MADE AS OF\s+(\d{2}-[A-Z]{3}-\d{2})/i);
  if (asOf) { st.setDate(asOf[1]); return; }

  // P&S TOTAL row -> realized P&L block
  if (/^TOTAL\b/.test(lineText) && /P&S/i.test(lineText)) {
    const fills = st.getPendingFills();
    const exp = (lineText.match(/EX-\s*(\d{2}-[A-Z]{3}-\d{2})/) || [])[1] || '';
    const buyTot = findIntInRange(words, cfg.psBuyXMin, cfg.psBuyXMax) ?? 0;
    const sellTot = findIntInRange(words, cfg.psSellXMin, cfg.psSellXMax) ?? 0;
    const matched = Math.max(buyTot, sellTot);
    const { amount, drcr } = parsePnlAmount(words);
    const realized = drcr === 'DR' ? -amount : amount;

    const buys = fills.filter(f => f.Direction === 'BUY');
    const sells = fills.filter(f => f.Direction === 'SELL');
    const avgBuy = wAvg(buys);
    const avgSell = wAvg(sells);
    const proto = fills[0];

    for (const f of fills) f.Expiration = exp;
    const block: PsBlock = {
      account: acct.accountNumber,
      date: proto?.Date || st.getDate(),
      contractType: proto?.Contract_Type || 'UNKNOWN',
      contractMonth: proto?.Contract_Month || '',
      expiration: exp,
      matchedQuantity: matched,
      avgBuyPrice: avgBuy,
      avgSellPrice: avgSell,
      realizedPnL: realized,
      perContractPnL: matched ? realized / matched : 0,
      pointValue: null, // filled by computeEconomics
      fills,
    };
    acct.psBlocks.push(block);
    st.setPendingFills([]);
    return;
  }

  // Fill row
  if (DATE_RE.test(words[0].text)) {
    const trade = parseFill(words, acct.accountNumber, 'PS', cfg);
    if (trade) {
      const fills = st.getPendingFills();
      fills.push(trade);
      st.setPendingFills(fills);
    }
  }
}

function handleJournalLine(words: PdfWord[], lineText: string, acct: AccountStatement): void {
  if (/^TOTAL\b/.test(lineText)) return;
  if (!DATE_RE.test(words[0].text)) return;
  const { amount, drcr } = parsePnlAmount(words);
  if (amount === 0 && drcr === null) return;
  // description: tokens between the "Payments" type marker and the REG/CCY columns
  const descWords = words.filter(w => w.x >= 200 && w.x <= 420).map(w => w.text);
  const typeWord = words.find(w => w.x >= 100 && w.x < 200);
  const description = descWords.join(' ').trim();
  acct.journal.push({
    account: acct.accountNumber,
    date: words[0].text,
    type: typeWord?.text || '',
    description,
    category: classifyJournal(description, drcr || 'DR'),
    amount,
    drcr: drcr || 'DR',
  });
}

/** Categorize a journal line from its description + DR/CR. No amounts hardcoded. */
function classifyJournal(description: string, drcr: 'DR' | 'CR'): import('./types').JournalCategory {
  const d = description.toUpperCase();
  if (/\bINT\b.*\bTSF\b|INT(ERNAL)? TRANSFER|\bTRANSFER\b/.test(d)) return 'TRANSFER';
  if (/CQG|RITHMIC|BUNDLE|LEVEL \d|NON ?PRO|MARKET DATA|\bAPI\b|DATA FEE/.test(d)) return 'DATA_FEE';
  if (drcr === 'CR') return 'DEPOSIT';      // Wire Tsf Recd, ACH, funds received
  return 'WITHDRAWAL';                       // other DR cash-out
}

function handleSummaryLine(
  words: PdfWord[], lineText: string, compact: string, acct: AccountStatement
): void {
  if (!acct.summary) acct.summary = blankSummary();
  const s = acct.summary;
  const { amount, drcr } = parsePnlAmount(words);
  const signed = drcr === 'DR' ? -amount : amount;
  const hasNum = words.some(w => /[\d.]/.test(w.text) && /^[\d,]+\.\d+$/.test(w.text));
  if (!hasNum) return;

  // Indented fee breakdown lines belong to the summary commission block
  const indented = words[0].x >= 24 && words[0].x < 40;

  if (compact.includes('TOTALCOMMISSION')) { ensureComm(s).total = amount; return; }
  if (indented && /^EXCHANGE/i.test(lineText)) { ensureComm(s).exchange = amount; return; }
  if (indented && /^NFA/i.test(lineText)) { ensureComm(s).nfa = amount; return; }
  if (indented && /^CLEARING/i.test(lineText)) { ensureComm(s).clearing = amount; return; }
  if (indented && /^CQG/i.test(lineText)) { ensureComm(s).cqgTrf = amount; return; }

  if (compact.includes('ACCOUNTCASHBALANCE')) { s.cashBalance ??= signed; return; }
  if (compact.includes('PAYMENTS/RECEIPTS') || compact.includes('PAYMENTSRECEIPTS') || compact.includes('PAYMENTS/RE')) { s.paymentsReceipts ??= signed; return; }
  if (compact.includes('REALIZEDPROFIT')) { s.realizedPnL ??= signed; return; }
  if (compact.startsWith('PREMIUMS')) { s.premiums ??= signed; return; }
  if (compact.includes('NEWCASHBALANCE')) { s.newCashBalance ??= signed; return; }
  if (compact.includes('OPENTRADEEQUITY')) { s.openTradeEquity ??= signed; return; }
  if (compact.includes('TOTALEQUITY')) { s.totalEquity ??= signed; return; }
  if (compact.includes('OPTIONMARKETVALUE')) { s.optionMarketValue ??= signed; return; }
  if (compact.includes('NETLIQUIDATINGVALUE')) { s.netLiquidatingValue ??= signed; return; }
  if (compact.includes('MAINTENANCEMARGIN')) { s.maintenanceMargin ??= signed; return; }
  if (compact.includes('INITIALMARGIN')) { s.initialMargin ??= signed; return; }
  if (compact.includes('MARGINDEFAULT') || compact.includes('MARGINEXCESS')) { s.marginExcess ??= signed; return; }
}

// ---------------------------------------------------------------------------
// Row parsing helpers
// ---------------------------------------------------------------------------

function parseFill(
  words: PdfWord[], account: string, section: 'CONFIRMATION' | 'PS', cfg: ExtractorConfig
): Trade | null {
  const buyMin = section === 'PS' ? cfg.psBuyXMin : cfg.confBuyXMin;
  const buyMax = section === 'PS' ? cfg.psBuyXMax : cfg.confBuyXMax;
  const sellMin = section === 'PS' ? cfg.psSellXMin : cfg.confSellXMin;
  const sellMax = section === 'PS' ? cfg.psSellXMax : cfg.confSellXMax;

  const date = words[0].text;
  let tradeNumber = '';
  let market = 'CME';
  let direction: 'BUY' | 'SELL' | '' = '';
  let quantity = 0;
  let price = '';
  let priceNum = 0;
  const contractParts: string[] = [];

  for (let i = 1; i < words.length; i++) {
    const text = words[i].text.trim();
    const x = words[i].x;

    if (/^\d{6,9}$/.test(text) && !tradeNumber && x >= 50 && x <= 95) { tradeNumber = text; continue; }
    if (MARKETS.includes(text.toUpperCase())) { market = text.toUpperCase(); continue; }
    if (text.toUpperCase() === 'USD') continue;

    if (/^\d{3,5}\.\d{1,5}$/.test(text) && x >= 410) { price = normalizePrice(text); priceNum = parseFloat(text); continue; }

    if (/^\d{1,3}$/.test(text)) {
      if (x >= buyMin && x <= buyMax) { direction = 'BUY'; quantity = parseInt(text, 10); continue; }
      if (x >= sellMin && x <= sellMax) { direction = 'SELL'; quantity = parseInt(text, 10); continue; }
    }

    const isMonth = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'].includes(text.toUpperCase());
    const isTwoDigitYear = /^\d{2}$/.test(text) && !!tradeNumber;
    const isKeyword = /^(future|futures|option|options|call|put|c|p)$/i.test(text);
    const isDescWord = /^[A-Za-z0-9&\-\/\(\)\$]+$/.test(text) && text.length > 1 && !/^\d{4,}/.test(text);
    if (isMonth || isTwoDigitYear || isKeyword || isDescWord) contractParts.push(text);
  }

  if (!date || !tradeNumber || !direction || !quantity) return null;

  const { contractType, contractMonth, instrumentType, optionType, originalDescription } =
    parseContractInfo(contractParts);

  return {
    Account: account,
    Section: section,
    Date: date,
    Trade_Number: tradeNumber,
    Market: market,
    Direction: direction,
    Quantity: quantity,
    Contract_Type: contractType,
    Contract_Month: contractMonth,
    Instrument_Type: instrumentType,
    Option_Type: optionType,
    Expiration: '',
    Trade_Price: price,
    Price_Num: priceNum,
    Commission: 0,
    Currency: 'USD',
    Original_Description: originalDescription,
  };
}

/** A fee line is: <label words @~400-475> USD <amount> <DR|CR>. */
function parseFeeLine(words: PdfWord[]): FeeLine | null {
  const usdIdx = words.findIndex(w => w.text.toUpperCase() === 'USD' && w.x >= 470 && w.x <= 495);
  if (usdIdx === -1) return null;
  const labelWords = words.slice(0, usdIdx).filter(w => w.x >= 395 && w.x < 480);
  if (labelWords.length === 0) return null;
  const after = words.slice(usdIdx + 1);
  const amtWord = after.find(w => /^[\d,]+\.\d{1,2}$/.test(w.text));
  if (!amtWord) return null;
  const drcrWord = after.find(w => w.text === 'DR' || w.text === 'CR');
  return {
    label: labelWords.map(w => w.text).join(' ').replace(/\.$/, ''),
    amount: parseNum(amtWord.text),
    drcr: (drcrWord?.text as 'DR' | 'CR') || 'DR',
  };
}

/** Find the money amount + DR/CR on a TOTAL or journal/summary line. */
function parsePnlAmount(words: PdfWord[]): { amount: number; drcr: 'DR' | 'CR' | null } {
  const amtWords = words.filter(w => /^[\d,]+\.\d{2}$/.test(w.text));
  if (amtWords.length === 0) return { amount: 0, drcr: null };
  // prefer the right-most amount that precedes a DR/CR token
  const drcrWord = words.find(w => w.text === 'DR' || w.text === 'CR');
  // choose the amount with the largest x that is still left of the duplicated col (~<500 first copy)
  const amt = amtWords.reduce((best, w) => (w.x < 500 && w.x > best.x ? w : best), amtWords[0]);
  return { amount: parseNum(amt.text), drcr: (drcrWord?.text as 'DR' | 'CR') ?? null };
}

// ---------------------------------------------------------------------------
// Word / line utilities
// ---------------------------------------------------------------------------

export function extractWordsWithPositions(
  items: (TextItem | TextMarkedContent)[], pageHeight: number, page: number
): PdfWord[] {
  const words: PdfWord[] = [];
  for (const item of items) {
    if (!('str' in item) || !item.str.trim()) continue;
    const textItem = item as TextItem;
    const x = textItem.transform[4];
    const y = pageHeight - textItem.transform[5];
    const width = textItem.width;
    const height = textItem.height;
    const parts = textItem.str.split(/\s+/).filter(Boolean);
    let currentX = x;
    for (const text of parts) {
      words.push({ text: text.trim(), x: currentX, y, width: width / parts.length, height, page });
      currentX += width / parts.length;
    }
  }
  return words;
}

export function groupWordsIntoLines(words: PdfWord[]): PdfLine[] {
  const lineMap = new Map<number, PdfWord[]>();
  const tol = 3;
  for (const word of words) {
    let foundY: number | null = null;
    for (const y of lineMap.keys()) {
      if (Math.abs(word.y - y) <= tol) { foundY = y; break; }
    }
    if (foundY !== null) lineMap.get(foundY)!.push(word);
    else lineMap.set(word.y, [word]);
  }
  return Array.from(lineMap.entries())
    .map(([y, w]) => ({ y, page: w[0].page, words: w }))
    .sort((a, b) => a.y - b.y);
}

export function detectAccountNumber(words: PdfWord[]): string {
  const hit = words.find(w => w.x >= 376 && w.x <= 398 && ACCOUNT_RE.test(w.text));
  return hit?.text || '';
}

export function detectMarker(compact: string): Section | null {
  if (compact.includes('PURCHASE&SALE')) return 'PS';
  if (compact.includes('TRADESCONFIRMATIONS') || compact.includes('TRADESHAVEBEENEXECUTED')) return 'CONFIRMATION';
  if (compact.includes('JOURNALENTRIES')) return 'JOURNAL';
  if (compact.includes('ACCOUNTSUMMARY')) return 'SUMMARY';
  return null;
}

function capturePeriod(acct: AccountStatement, lineText: string): void {
  const slash = lineText.match(/(\d{2}\/\d{2}\/\d{2})\s*(?:T\s*O|TO)\s*(\d{2}\/\d{2}\/\d{2})/i);
  if (slash) { acct.period.from ||= slash[1]; acct.period.to ||= slash[2]; return; }
  const dash = lineText.match(/(\d{2}-[A-Z]{3}-\d{2})\s*(?:T\s*O|TO)\s*(\d{2}-[A-Z]{3}-\d{2})/i);
  if (dash) { acct.period.from ||= dash[1]; acct.period.to ||= dash[2]; }
}

function findIntInRange(words: PdfWord[], min: number, max: number): number | null {
  const hit = words.find(w => w.x >= min && w.x <= max && /^\d{1,4}$/.test(w.text));
  return hit ? parseInt(hit.text, 10) : null;
}

function lastNumber(words: PdfWord[]): number | null {
  for (let i = words.length - 1; i >= 0; i--) {
    if (/^[\d,]+\.\d+$/.test(words[i].text)) return parseNum(words[i].text);
  }
  return null;
}

function wAvg(fills: Trade[]): number {
  const q = fills.reduce((s, f) => s + f.Quantity, 0);
  if (!q) return 0;
  return fills.reduce((s, f) => s + f.Price_Num * f.Quantity, 0) / q;
}

function parseNum(s: string): number { return parseFloat(s.replace(/,/g, '')); }
function normalizePrice(s: string): string { return s; }

// ---------------------------------------------------------------------------
// Fee / summary helpers
// ---------------------------------------------------------------------------

export function emptyFees(): FeeBreakdown {
  return { lines: [], exchange: 0, nfa: 0, clearing: 0, cqgTrf: 0, commission: 0, other: 0, total: 0 };
}

function foldFee(fb: FeeBreakdown, fee: FeeLine): void {
  const v = fee.drcr === 'CR' ? -fee.amount : fee.amount;
  const l = fee.label.toUpperCase();
  if (l.startsWith('EXCHANGE')) fb.exchange += v;
  else if (l.startsWith('NFA')) fb.nfa += v;
  else if (l.startsWith('CLEARING')) fb.clearing += v;
  else if (l.startsWith('CQG')) fb.cqgTrf += v;
  else if (l.startsWith('COMMISSION')) fb.commission += v;
  else fb.other += v;
  fb.total += v;
}

function ensureComm(s: AccountSummary): FeeBreakdown {
  if (!s.commission) s.commission = emptyFees();
  return s.commission;
}

function blankSummary(): AccountSummary {
  return {
    cashBalance: null, paymentsReceipts: null, realizedPnL: null, premiums: null,
    newCashBalance: null, openTradeEquity: null, totalEquity: null, optionMarketValue: null,
    netLiquidatingValue: null, maintenanceMargin: null, initialMargin: null, marginExcess: null,
    commission: null,
  };
}

function blankAccount(num: string): AccountStatement {
  return {
    accountNumber: num,
    period: { from: '', to: '' },
    trades: [],
    confirmations: [],
    psBlocks: [],
    positions: [],
    openPositions: [],
    journal: [],
    cashFlow: { receipts: 0, payments: 0, net: 0, deposits: 0, withdrawals: 0, transfersNet: 0, dataFees: 0 },
    fees: emptyFees(),
    summary: null,
    economics: [],
    reconciliation: {
      psBlockPnLSum: 0, summaryRealizedPnL: null, pnlMatches: null,
      blockFeesTotal: 0, summaryCommissionTotal: null, feesMatch: null,
      cashFlowNet: 0, summaryPaymentsReceipts: null, cashFlowMatches: null,
      flat: true, openTradeEquity: null,
    },
    buyQty: 0,
    sellQty: 0,
  };
}
